import { useState, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import ConfigSurveyService from 'services/ServiceSATISFACTIONSURVEY';

const selectableOptionSchema = yup
  .object({
    label: yup.string().required(),
    value: yup.string().required()
  })
  .required();

const messageConfigSchema = yup.object().shape({
  title: yup.string().nullable(),
  button: yup.string().nullable(),
  body: yup.string().nullable(),
  caption: yup.string().nullable()
});

const optionNpsSchema = yup.object().shape({
  rate: yup.number().integer().min(0).nullable(),
  label: yup.string().nullable()
});

const optionTagSchema = yup.object().shape({
  label: yup.string().nullable(),
  rate: yup.number().nullable()
});

const optionGenericSchema = yup.lazy((value) => {
  if (value && typeof value === 'object' && 'rate' in value)
    return optionNpsSchema;
  if (value && typeof value === 'object' && 'tag' in value)
    return optionTagSchema;
  return yup.object().shape({
    label: yup.string().nullable(),
    rate: yup.number().integer().nullable()
  });
});

export const questionSchema = yup.object().shape({
  messageListConfig: messageConfigSchema.required(),
  messageButtonConfig: messageConfigSchema.required(),
  isMain: yup.boolean().required(),
  id: yup.string().required('ID da questão é obrigatório'),
  name: yup.string().required('Nome da questão é obrigatório'),
  message: yup
    .string()
    .required('Todas as perguntas devem conter uma mensagem'),
  audioId: yup
    .mixed()
    .nullable()
    .transform((val) => (val == 'undefined' ? null : val))
    .when('type', {
      is: 'FREE_TEXT',
      then: (schema) =>
        schema.oneOf(
          [null, undefined],
          'Questões do tipo Texto Livre não podem ter áudio'
        )
    }),
  type: yup
    .string()
    .oneOf(['NPS', 'TAG', 'FREE_TEXT', 'CSAT'], 'Tipo de questão inválido')
    .required('Tipo de questão é obrigatório'),

  options: yup
    .array()
    .of(optionGenericSchema)
    .when('type', (type, schema) => {
      switch (type) {
        case 'NPS':
          return schema
            .min(10, 'Questões do tipo NPS devem ter exatamente 10 respostas')
            .max(10, 'Questões do tipo NPS devem ter exatamente 10 respostas')
            .required('Opções são obrigatórias para NPS');

        case 'CSAT':
          return schema
            .min(5, 'Questões do tipo CSAT devem ter exatamente 5 respostas')
            .max(5, 'Questões do tipo CSAT devem ter exatamente 5 respostas')
            .required('Opções são obrigatórias para CSAT');

        case 'FREE_TEXT':
          return schema.max(
            0,
            'Questões do tipo TEXTO LIVRE não podem ter opções'
          );

        case 'TAG':
          return schema
            .min(1, 'Questões do tipo TAG precisam ter pelo menos uma resposta')
            .of(
              yup.object().shape({
                label: yup.string().nullable(),
                rate: yup
                  .number()
                  .integer()
                  .max(10, 'A maior nota permitida é 10')
                  .min(0, 'A menor nota permitida é 0')
                  .nullable()
              })
            );

        default:
          return schema;
      }
    })
});

export const queueSchema = yup.object().shape({
  _id: yup.string().nullable(),
  id: yup.number().integer().required('ID da fila é obrigatório'),
  name: yup.string().required('Nome da fila é obrigatório')
});

export const surveyDataSchema = yup.object().shape({
  active: yup.boolean().required('Campo active é obrigatório'),
  initialMessage: yup.string().nullable(),
  finalMessage: yup.string().nullable(),
  sendToActiveContact: yup.boolean().required(),
  quantityOfInvalidAttempts: yup.number().integer().nullable(),
  incorrectOptionMessage: yup.string().nullable(),
  _id: yup.string().nullable(),
  name: yup.string().required('Nome da pesquisa é obrigatório'),
  queues: yup.array().of(queueSchema).nullable(),
  initialAudioId: yup
    .mixed()
    .nullable()
    .transform((val) => (val == 'undefined' ? null : val)),
  finalAudioId: yup
    .mixed()
    .nullable()
    .transform((val) => (val == 'undefined' ? null : val)),
  reminderToResend: yup.boolean().nullable(),
  reminderAttempts: yup.number().integer().nullable(),
  reminderTime: yup.number().integer().nullable(),
  reminderMessage: yup.string().nullable(),
  questions: yup.array().of(questionSchema).required('Questions é obrigatório'),
  __v: yup.number().integer().nullable()
});

const formResolverSchema = surveyDataSchema.omit(['active']).shape({
  questions: yup
    .array(
      questionSchema.shape({
        type: selectableOptionSchema,
        id: yup.string().optional()
      })
    )
    .required()
    .min(1, 'Pelo menos uma pergunta deve estar presente na pesquisa'),
  queues: yup
    .array(selectableOptionSchema)
    .required('Pelo menos uma fila é necessária para a pesquisa')
    .min(1, 'Pelo menos uma fila é necessária para a pesquisa')
});

const subResponseSchema = yup.object().shape({
  id: yup.number().integer().required(),
  id_satisfaction: yup.string().required(),
  type: yup.string().oneOf(['TAG', 'NPS', 'CSAT', 'FREE_TEXT']).required(),
  rate: yup.number().nullable(),
  customer_response: yup.string().nullable(),
  tag: yup.string().nullable(),
  label: yup.string().nullable(),
  date_of_response: yup.string().required()
});

const analyticalReportItemSchema = yup.object().shape({
  date: yup.string().required(),
  rate: yup.string().required(),
  name: yup.string().default('').defined(),
  tag: yup.string().nullable(),
  agentName: yup.string().defined(),
  customer: yup.string().nullable(),
  customerPhone: yup.string().nullable(),
  customerPhoneDDI: yup.string().nullable(),
  queueName: yup.string().required(),
  typeSurvey: yup.string().required(),
  channel: yup.string().required(),
  attendanceId: yup.string().required(),
  subResponses: yup.array().of(subResponseSchema).required()
});

const analyticalReportResponseSchema = yup.object().shape({
  list: yup.array().of(analyticalReportItemSchema).required(),
  total: yup.number().integer().required()
});

const useSatisfactionSurvey = () => {
  const [state, setState] = useState({
    response: {
      config: [],
      analytical: []
    },
    pagination: {
      config: {
        totalRegisters: 0,
        activePage: 1
      },
      analytical: {
        totalRegisters: 0,
        activePage: 1
      }
    },
    isLoading: false,
    isError: false,
    error: null
  });

  const setLoading = (loading = true) =>
    setState((prev) => ({
      ...prev,
      isLoading: loading,
      isError: false,
      error: null
    }));

  const setError = (err) =>
    setState((prev) => ({
      ...prev,
      isLoading: false,
      isError: true,
      error: err
    }));

  const fetchAllConfigs = useCallback(
    async (selectedPage = state.pagination.config.activePage, perPage = 10) => {
      setLoading(true);
      try {
        const response = await ConfigSurveyService.getAll(
          selectedPage,
          perPage
        );
        const data = response?.data ?? [];
        const total = Array.isArray(data)
          ? data.length
          : response?.data?.totalRegisters ?? 0;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          response: {
            ...prev.response,
            config: data
          },
          pagination: {
            ...prev.pagination,
            config: {
              totalRegisters: total,
              activePage: selectedPage
            }
          }
        }));

        return data;
      } catch (err) {
        console.error('Erro na listagem de surveys:', err);
        toast.error('Não foi possível listar as configurações', {
          autoClose: 3000,
          closeOnClick: true
        });
        setError(err);
        return null;
      }
    },
    [state.pagination.config.activePage]
  );

  const fetchGeneralConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response =
        await ConfigSurveyService.getSurveyGeneralConfigurations();
      setLoading(false);
      return response.data;
    } catch (err) {
      console.error('Erro ao buscar configurações gerais', err);
      toast.error(
        err.response?.data?.msg ||
          'Erro ao carregar configurações, por favor entre em contato com o suporte',
        { autoClose: 3000, closeOnClick: true }
      );
      setError(err);
      return null;
    }
  }, []);

  const updateGeneralConfigs = useCallback(async (data) => {
    setLoading(true);
    try {
      const response =
        await ConfigSurveyService.updateSurveyGeneralConfigurations(data);
      toast.info(
        `Configurações gerais de pesquisa: ${
          response.data.msg || 'Atualizadas'
        }`
      );
      setLoading(false);
      return response.data;
    } catch (err) {
      const msg = err?.response?.data?.msg;
      const msgReturn = `Não é possível atualizar as configurações. ${
        msg || ''
      }`;
      toast.error(msgReturn, { autoClose: 3000, closeOnClick: true });
      setError(err);
      throw err;
    }
  }, []);

  const createSurvey = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await ConfigSurveyService.create(data);
        toast.success(
          `Configuração de pesquisa criada: ${response.data.msg || 'OK'}`,
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
        await fetchAllConfigs(1);
        setLoading(false);
        return response.data;
      } catch (err) {
        console.error('Erro ao criar survey:', err);
        const errorMessage =
          err.name === 'ValidationError'
            ? err.errors.join(', ')
            : err.response?.data?.msg ||
              'Não é possível cadastrar essa configuração.';
        toast.error(errorMessage, { autoClose: 3000, closeOnClick: true });
        setError(err);

        // NÃO navegar ou recarregar quando houver erro
        // Mantém os dados do formulário para o usuário corrigir
        setLoading(false);

        throw err;
      }
    },
    [fetchAllConfigs]
  );

  const updateSurvey = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        if (!id) throw new Error('ID é obrigatório para atualização');

        const response = await ConfigSurveyService.update(id, data);
        toast.info(
          `Configuração de pesquisa atualizada: ${response.data.msg || 'OK'}`,
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );

        if (response.data?.edit) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            response: {
              ...prev.response,
              config: prev.response.config.map((item) =>
                item._id === id ? response.data.edit : item
              )
            }
          }));
        } else {
          await fetchAllConfigs(state.pagination.config.activePage || 1);
        }

        return response.data;
      } catch (err) {
        console.error('Erro ao atualizar survey:', err);
        const errorMessage =
          err.name === 'ValidationError'
            ? err.errors.join(', ')
            : err.response?.data?.msg ||
              'Não é possível atualizar essa configuração.';
        toast.error(errorMessage, { autoClose: 3000, closeOnClick: true });
        setError(err);

        // NÃO recarregar os dados quando houver erro
        // Apenas marcar como não está mais carregando
        setLoading(false);

        throw err;
      }
    },
    [fetchAllConfigs, state.pagination.config.activePage]
  );

  const toggleConfigStatus = useCallback(
    async (id, currentStatus) => {
      try {
        const newStatus = { active: !currentStatus };
        const response = await ConfigSurveyService.enableConfig(id, newStatus);
        if (response.data?.msg) {
          toast.info(`Alteração realizada com sucesso: ${response.data.msg}`, {
            autoClose: 3000,
            closeOnClick: true
          });
          await fetchAllConfigs(state.pagination.config.activePage || 1);
        }
        return response.data;
      } catch (err) {
        console.error('Erro ao alternar status da survey:', err);
        toast.error(
          err?.response?.data?.msg ?? 'Erro ao alterar status da pesquisa',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
        throw err;
      }
    },
    [fetchAllConfigs, state.pagination.config.activePage]
  );

  const getSurveyById = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await ConfigSurveyService.get(id);
      const data = response?.data;
      const { success, error } = surveyDataSchema.safeValidateSync(data);

      if (!success) {
        console.log({ error }, { depth: null });

        return toast.error(
          'Não foi possível encontrar essa pesquisa de satisfação'
        );
      }

      setLoading(false);
      return data;
    } catch (err) {
      console.error('Erro ao buscar survey por id:', err);
      const errorMessage =
        err.name === 'ValidationError'
          ? err.errors.join(', ')
          : err.response?.data?.msg ||
            'Erro ao exibir configuração, por favor entrar em contato com o suporte';
      toast.error(errorMessage, { autoClose: 3000, closeOnClick: true });
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalyticalReport = useCallback(
    async (dataToSend, selectedPage = 1) => {
      setLoading(true);

      try {
        const response = await ConfigSurveyService.fetchReports({
          ...dataToSend,
          page: selectedPage
        });

        const validationResult =
          analyticalReportResponseSchema.safeValidateSync(response.data);

        if (!validationResult.success) {
          console.error(
            'Erro na validação dos dados do relatório:',
            validationResult.error
          );
          throw new Error('Dados do relatório em formato inválido');
        }

        const { list = [], total = 0 } = validationResult.data;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          response: {
            ...prev.response,
            analytical: list
          },
          pagination: {
            ...prev.pagination,
            analytical: {
              totalRegisters: total,
              activePage: selectedPage
            }
          }
        }));

        return { analytical: list };
      } catch (err) {
        console.error('Erro ao buscar relatório analítico:', err);
        toast.error(
          err.response?.data?.msg ||
            err.message ||
            'Erro ao buscar relatório analítico. Por favor, entre em contato com o suporte.',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const options = useMemo(
    () =>
      state.response.config.map((item) => ({
        label: item.name || item._id || 'Sem nome',
        value: item._id ?? item.id ?? item.name
      })),
    [state.response.config]
  );

  useEffect(() => {
    fetchAllConfigs();
  }, []);

  return {
    ...state,
    totalRegisters: state.pagination.config.totalRegisters,
    activePage: state.pagination.config.activePage,
    fetchAllConfigs,
    fetchGeneralConfigs,
    updateGeneralConfigs,
    toggleConfigStatus,
    getSurveyById,
    createSurvey,
    updateSurvey,
    fetchAnalyticalReport,
    options,
    schemas: {
      surveyDataSchema,
      questionSchema,
      formResolverSchema,
      analyticalReportResponseSchema,
      analyticalReportItemSchema,
      subResponseSchema
    }
  };
};

export default useSatisfactionSurvey;
